// NexradBar — small floating control surface for the NEXRAD radar
// time scrubber. Shows when the 'nexrad' layer is on. Range -60..0
// minutes in 5-minute increments. 0 = live frame (Iowa State Mesonet
// auto-refreshes server-side every ~5 min); negative = archived
// frame at YYYYMMDDHHMM.

import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useInstrumentPrefsStore } from '../stores/instrumentPrefsStore';
import { useInstrumentStripHeight } from './useInstrumentStripHeight';

const SLIDER_MIN = -60;
const SLIDER_MAX = 0;
const SLIDER_STEP = 5;

export default function NexradBar() {
  const showNexrad = useLayerVisibilityStore((s) => s.layers.nexrad);
  // Time scrubber only applies to the 'classic' n0q archive (Iowa Mesonet
  // ridge::USCOMP-N0Q-YYYYMMDDHHMI). The MRMS composite and Low-Tilt
  // variants only have a 'current' frame, so hide the scrubber when those
  // are selected. (Boss 2026-05-21 19:42 PDT.)
  const nexradVariant = useUiStore((s) => s.nexradVariant);
  const showWxHazards = useLayerVisibilityStore((s) => s.layers.wxhazards);
  const showInstruments = useInstrumentPrefsStore((s) => s.showInstruments);
  const nexradOffsetMin = useUiStore((s) => s.nexradOffsetMin);
  const setNexradOffsetMin = useUiStore((s) => s.setNexradOffsetMin);

  // Live height of the bottom instrument strip so this bar can stack
  // ABOVE it without overlap. Hook short-circuits when this bar isn't
  // visible so we don't waste a ResizeObserver.
  const instrumentH = useInstrumentStripHeight(showNexrad);

  if (!showNexrad) return null;
  if (nexradVariant !== 'classic') return null;

  // Compute vertical position. When the GFA hazard bar is visible
  // it occupies ~110 px above the instrument bar (chip card +
  // scrubber card + gaps); we sit above that.
  const INSTRUMENT_BOTTOM_OFFSET = 12;
  const ABOVE_INSTRUMENTS_GAP_PX = 14;
  const HAZARD_BAR_FOOTPRINT_PX = 110;
  const baseBottomPx = showInstruments
    ? INSTRUMENT_BOTTOM_OFFSET + instrumentH + ABOVE_INSTRUMENTS_GAP_PX
    : 14;
  const bottomPx = showWxHazards ? baseBottomPx + HAZARD_BAR_FOOTPRINT_PX : baseBottomPx;

  // Tick label positions. Place 'now' at right (100%), '-60m' at
  // left (0%), '-30m' at center (50%) to match a standard radar
  // loop scrubber.
  const span = SLIDER_MAX - SLIDER_MIN;          // 60
  const pctNow = ((0 - SLIDER_MIN) / span) * 100;       // 100
  const pctMid = ((-30 - SLIDER_MIN) / span) * 100;     // 50

  const offsetLabel = nexradOffsetMin === 0
    ? 'now'
    : `${nexradOffsetMin}m`;
  const wallClock = wallClockAt(nexradOffsetMin);

  return (
    <div
      className="absolute left-0 right-0 z-10 flex justify-center pointer-events-none"
      style={{ bottom: `${bottomPx}px` }}
    >
      <div className="pointer-events-auto bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-bar px-3 py-2 w-[min(28rem,calc(100vw-1.5rem))]">
        <div className="flex items-center gap-3">
          <div className="flex flex-col items-start min-w-[5rem]">
            <span className="text-[9px] tracking-widest text-cockpit-400 uppercase">Radar</span>
            <span className="font-mono text-[12px] text-ember-300 leading-none mt-0.5">
              {offsetLabel}
            </span>
          </div>
          <div className="flex-1">
            <input
              type="range"
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              step={SLIDER_STEP}
              value={nexradOffsetMin}
              onChange={(e) => setNexradOffsetMin(parseInt(e.currentTarget.value, 10))}
              aria-label="NEXRAD time offset"
              className="w-full accent-ember-500"
            />
            <div className="relative h-3 mt-0.5 text-[9px] font-mono text-cockpit-400">
              <span className="absolute" style={{ left: '0%' }}>-60m</span>
              <span className="absolute -translate-x-1/2" style={{ left: `${pctMid}%` }}>-30m</span>
              <span className="absolute -translate-x-full" style={{ left: `${pctNow}%` }}>now</span>
            </div>
          </div>
          <div className="text-right min-w-[3.75rem]">
            <span className="text-[9px] tracking-widest text-cockpit-400 uppercase">Time</span>
            <div className="font-mono text-[11px] text-cockpit-100 leading-none mt-0.5">{wallClock}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function wallClockAt(offsetMin: number): string {
  const t = new Date(Date.now() + offsetMin * 60_000);
  const hh = String(t.getUTCHours()).padStart(2, '0');
  const mm = String(t.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}Z`;
}
